/** @prettier */

import React, { useMemo, useState, useRef } from 'react';
import PageRail from '@surfline/quiver-react/dist/PageRail';
import { useAnimation, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { loadTreatment } from '@surfline/web-common/dist/features/redux';
import { useDeviceSize } from '@surfline/quiver-react';

import CollectionList from '../../../components/CollectionList';
import CollectionRail from '../../../components/CollectionRail';
import CompanionAd from '../../../components/CompanionAd';
import MultiCam from '../../../components/MultiCam';
import userSettingsPropType from '../../../propTypes/userSettings';
import { SL_MULTI_CAM_ADS } from '../../../common/treatments';
import { useReduxAction } from '../../../hooks/useReduxAction';
import { useKeyPress } from '../../../hooks/useKeyPress';
import { useFullscreen } from '../../../hooks/useFullscreen';
import { useFramerAnimation } from '../../../hooks/useFramerAnimation';
import { useToggle } from '../../../hooks/useToggle';
import { usePageCall } from '../../../hooks/usePageCall';
import { getActiveCollection } from '../../../selectors/multiCam';
import SurfCamsHelmet from '../../../components/SurfCamsHelmet';
import { fetchCollections } from '../../../actions/multiCam';
import { getPremiumStatus, getUserEntitlements } from '../../../selectors/user';

const getClass = (isFullscreen, rightRailIsOpen, showDesktopAd, showMobileAd) =>
  classNames({
    'sl-multi-cam-page': true,
    'sl-multi-cam-page--fullscreen': isFullscreen,
    'sl-multi-cam-page--right-rail': rightRailIsOpen,
    'sl-multi-cam-page--desktop-ad': showDesktopAd,
    'sl-multi-cam-page--mobile-ad': showMobileAd,
  });

const SurfCamsPage = React.memo(
  ({
    collectionsLoading,
    settings,
    doSetSelectedSpots,
    selectedSpots,
    primarySpotIndex,
    doDecrementPrimarySpotIndex,
    doIncrementPrimarySpotIndex,
  }) => {
    /* companion ad react key so that we can refresh the ad after every pre-roll */
    const [companionAdKey, setCompanionAdKey] = useState(0);

    /** companion ad logic */
    const dispatch = useDispatch();
    const showAdsTreatment = dispatch(loadTreatment(SL_MULTI_CAM_ADS)) === 'on';
    const { isDesktop } = useDeviceSize();
    const isPremium = useSelector(getPremiumStatus);
    const entitlements = useSelector(getUserEntitlements, shallowEqual);

    /** Page Call */
    const properties = useRef({ category: 'cams & reports' });
    usePageCall('multi-cam', properties.current);

    const multiCamElement = useRef(null);

    // eslint-disable-next-line no-unused-vars
    const [rightRailIsOpen, _, toggleRightRail] = useToggle(false);

    const { isFullscreen, toggleFullscreen } = useFullscreen(multiCamElement?.current);

    const activeCollection = useSelector(getActiveCollection, shallowEqual);
    const showRightRail = !collectionsLoading && !!activeCollection;

    /** Animations */
    const leftRailSpinnerControls = useAnimation();
    const rightRailControls = useAnimation();
    const leftRailControls = useAnimation();
    const [startAnimations] = useFramerAnimation({
      controls: leftRailSpinnerControls,
      variant: 'hidden',
      start: showRightRail,
    });
    useFramerAnimation({
      controls: rightRailControls,
      variant: 'loaded',
      start: activeCollection?.fetchedSpots && !activeCollection?.loading,
      reset: activeCollection?._id,
    });
    const [leftRailAnimationComplete] = useFramerAnimation({
      controls: leftRailControls,
      variant: 'shown',
      start: startAnimations,
    });

    /** Redux Actions */
    const shouldResetSelectedSpots =
      !!activeCollection && !collectionsLoading && activeCollection.fetchedSpots;
    useReduxAction({
      action: doSetSelectedSpots,
      memoizedArgs: useMemo(() => [activeCollection], [activeCollection]),
      condition: shouldResetSelectedSpots,
    });

    /** Keyboard Event Listeners (Vim Keybinds for Jason Wang) */
    useKeyPress(doDecrementPrimarySpotIndex, 'ArrowLeft', 'h', 'H');
    useKeyPress(doIncrementPrimarySpotIndex, 'ArrowRight', 'l', 'L');

    const canMountCams = leftRailAnimationComplete;

    const showDesktopAd = !isPremium && showAdsTreatment && isDesktop;
    const showMobileAd = !isPremium && showAdsTreatment && !isDesktop;

    return (
      <div
        className={getClass(isFullscreen, rightRailIsOpen, showDesktopAd, showMobileAd)}
        ref={multiCamElement}
      >
        <SurfCamsHelmet />
        <PageRail side="left">
          <CollectionList
            activeCollection={activeCollection}
            leftRailControls={leftRailControls}
            spinnerControls={leftRailSpinnerControls}
          />
        </PageRail>
        <MultiCam
          canMountCams={canMountCams}
          isFullscreen={isFullscreen}
          toggleFullscreen={toggleFullscreen}
          toggleRightRail={toggleRightRail}
          rightRailIsOpen={rightRailIsOpen}
          companionAdKey={companionAdKey}
          setCompanionAdKey={setCompanionAdKey}
        >
          {showMobileAd && (
            <CompanionAd
              adConfigName="sl-multi-cam-companion-mobile"
              entitlements={entitlements}
              key={companionAdKey}
            />
          )}
        </MultiCam>
        <PageRail side="right">
          <motion.div className="sl-collection-rail-container">
            <CollectionRail
              settings={settings}
              activeCollection={activeCollection}
              primarySpotId={selectedSpots[primarySpotIndex] && selectedSpots[primarySpotIndex]._id}
              selectedSpots={selectedSpots}
              collectionControls={rightRailControls}
              companionAdKey={companionAdKey}
            />
          </motion.div>
          {showDesktopAd && (
            <CompanionAd
              adConfigName="sl-multi-cam-companion"
              entitlements={entitlements}
              key={companionAdKey}
            />
          )}
        </PageRail>
      </div>
    );
  }
);

SurfCamsPage.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  activeCollection: PropTypes.shape({
    name: PropTypes.string,
  }),
  selectedSpots: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      cameras: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ).isRequired,
  doSetSelectedSpots: PropTypes.func.isRequired,
  collectionsLoading: PropTypes.bool.isRequired,
  settings: userSettingsPropType.isRequired,
  primarySpotIndex: PropTypes.number.isRequired,
  doDecrementPrimarySpotIndex: PropTypes.func.isRequired,
  doIncrementPrimarySpotIndex: PropTypes.func.isRequired,
  staticContext: PropTypes.shape({
    status: PropTypes.number,
    url: PropTypes.string,
  }),
};

SurfCamsPage.defaultProps = {
  activeCollection: null,
  staticContext: {},
};

// SRR Fetch
SurfCamsPage.fetch = [
  (_, req) => dispatch => dispatch(fetchCollections(null, req.cookies)),
];

export default SurfCamsPage;
